/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory, useLocation } from 'react-router-dom';
import { Dispatch, Fragment, SetStateAction, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import {
  message,
  Tag as TagAntd,
  Popover,
  Tooltip,
  Dropdown,
  Typography,
  Button,
  Empty,
} from 'antd';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import {
  idOrNull,
  toggleFolderNotify,
  toggleVFileNotify,
} from '~src/services/api';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProjectBuilder } from '~src/hooks/api-hooks/useProjectBuilder';
import {
  dbFileToTableEntry,
  dbFolderToTableEntry,
  sortByModified,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { DbFile, DbFolder, DbFocusOptions, DbModuleFile } from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { uniq } from 'lodash';
import { queryCache, useMutation, useQueryCache } from 'react-query';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import _ from 'lodash';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { getTagsForFile } from '~src/utils/tags';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { ColumnType } from 'antd/es/table/interface';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import doReturnTheme from '~src/styles/themeEngine';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { TenderTabsProps } from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { SVFIconType } from '../Viewers/SVFViewer/components/SVFIconType';

export type FileTableProps = TableProps<TableEntry> &
  Pick<TenderTabsProps, 'selectedKeys' | 'onSelect' | 'onSelectAll'> & {
    highlightedRow?: TableEntry;
    suMode?: boolean;
    setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
    handleRow: Required<FileTableProps>['onRow'];
    onEmptyOverlay?: () => void;
    focusOptions?: DbFocusOptions[];
    openSVFTranscribe?: (fileToView: DbFile | DbModuleFile) => void;
    onInitializeFromTemplate?: () => void;
    showInitializeButton?: boolean;
    onOpenPermissions?: () => void;
    onNavigateToProjectBuilder?: () => void;
  };
/** Default FileTable, used with folder navigation */
export const FileTable: React.FC<FileTableProps> = ({
  selectedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  suMode,
  handleRow,
  setSelectedItems,
  onEmptyOverlay,
  focusOptions,
  openSVFTranscribe,
  onInitializeFromTemplate,
  showInitializeButton,
  onOpenPermissions,
  onNavigateToProjectBuilder,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const currentFolder = useCurrentFolder();
  const folderId = currentFolder?.id || null;
  const permissionLevel = currentFolder?.permission || 0;
  const projectId = useProjectId();
  const history = useHistory();
  const { pathname } = useLocation();
  const archiveSettings = useArchiveSettings();
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;
  const tagsData = archiveSettings.data?.resTags;
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { fileToView, setFileToView } = useFileViewerContext();
  const { currentTheme } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);

  const filesQueryBaseKeys = {
    folderId: idOrNull(folderId),
    showDeleted: suMode,
    withVersions: true,
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
  };

  const { sortByRevision } = useSortByRevision();
  const foldersQueryBaseKeys = {
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  };

  const filesQuery = useFiles(filesQueryBaseKeys, {
    enabled: !!currentFolder && permissionLevel > 1,
    onError: () =>
      // go to /archive
      history.replace(`/projects/${projectId}/archive`),
  });
  const foldersQuery = useFolders(foldersQueryBaseKeys);
  // hasArchiveFolders is only ever read below inside the isArchiveRoot empty
  // state branch, so only fetch it there - it 403s for users without
  // Project Builder / archive settings access (e.g. Manager accounts) and is
  // otherwise wasted on every other route (including the plain dashboard).
  const isArchiveRoot = pathname.includes('/archive') && !currentFolder;
  const projectBuilderQuery = useProjectBuilder(
    { skip: 0, take: 1 },
    { enabled: isArchiveRoot }
  );
  const hasArchiveFolders = (projectBuilderQuery.data?.length || 0) > 0;

  const [
    doUpdateFileNotifications,
    updateFileNotificationMutation,
  ] = useMutation(toggleVFileNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { versionedFileId }) => {
      queryCache.setQueryData<DbFile[]>(
        ['files', { ...filesQueryBaseKeys, projectId: String(projectId) }],
        (prevFiles = []) => {
          const changedFileIndex = prevFiles.findIndex(
            (item) => item.id === versionedFileId
          );
          if (changedFileIndex >= 0) {
            const newList = [...prevFiles];
            newList[changedFileIndex] = {
              ...newList[changedFileIndex],
              is_notify,
            };
            return newList;
          }
          return prevFiles;
        }
      );
    },
  });

  const [
    doUpdateFolderNotifications,
    updateFolderNotificationMutation,
  ] = useMutation(toggleFolderNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { folderId }) => {
      queryCache.setQueryData<DbFolder[]>(
        ['folders', { ...foldersQueryBaseKeys, projectId: String(projectId) }],
        (prevFolders = []) => {
          const changedFolderIndex = prevFolders.findIndex(
            (item) => item.id === folderId
          );
          if (changedFolderIndex >= 0) {
            const newList = [...prevFolders];
            newList[changedFolderIndex] = {
              ...newList[changedFolderIndex],
              is_notify,
            };
            return newList;
          }
          return prevFolders;
        }
      );
    },
  });

  const searchContext = useContext(SearchContext);

  const foldersSource = useMemo(
    () =>
      (foldersQuery.data || [])
        .filter((v) => {
          return v.parent_id === folderId;
        })
        .map(dbFolderToTableEntry),
    [foldersQuery.data, folderId]
  );

  const filesSource = useMemo(
    () =>
      (filesQuery.data || [])
        .filter((item) => item.state !== DB_STATE.PARTIAL)
        .map((item) => dbFileToTableEntry(item)),
    [filesQuery.data]
  );

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const filteredFiles = [
    ...filesSource
      .filter((item) => {
        //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
        //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
        const dynamicIndex = (item.dbFile?.versions || []).findIndex(
          (el) =>
            //provided testing function
            el.version === fileVersionsContext[item.id]?.version &&
            fileVersionsContext[item.id]?.version
        );
        if (
          filterByQuery(item.name, searchContext.query) ||
          filterByQuery(item.description, searchContext.query) ||
          filterByQuery(item.type.icon, searchContext.query) ||
          filterByQuery(
            dynamicIndex > 0
              ? item.dbFile?.versions &&
                  item.dbFile?.versions[dynamicIndex] &&
                  item.dbFile?.versions[dynamicIndex].version
              : item.dbFile?.versions &&
                  item.dbFile?.versions[0] &&
                  item.dbFile?.versions[0].version,
            searchContext.query
          )
        ) {
          return item;
        }
        return null;
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  ];

  const getBackgroundRow = (record: TableEntry, isType?: boolean) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if ((record.key === highlightedRow?.key && data) || record.disabled) {
      return {
        color:
          window.sessionStorage.getItem('appState') === 'timeMachine'
            ? theme.colors.text.secondary
            : theme.colors.text.primary,
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    }

    return {
      color:
        window.sessionStorage.getItem('appState') === 'timeMachine' && data
          ? theme.colors.text.secondary
          : theme.colors.text.primary,
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const getBackgroundRowTypeMachine = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if ((record.key === highlightedRow?.key && data) || record.disabled) {
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    }

    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        width: 30,
        align: 'center',
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 60,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
          const isRootTenderFolder =
            record.dbFolder?.option?.is_tender_root_folder;
          const isPreviewableFormat = getIsFilePreviewableByIcon(type.icon);

          return {
            props: {
              style:
                window.sessionStorage.getItem('appState') !== 'timeMachine'
                  ? getBackgroundRow(record)
                  : getBackgroundRowTypeMachine(record),
            },
            children: (
              <Fragment>
                {/* Root tender folder displaying logic */}
                {isTenderFolder && isRootTenderFolder && (
                  <Tooltip
                    placement="bottom"
                    title={intl.formatMessage({
                      id: 'tender-folder',
                    })}>
                    <div css={styles.outerEndAlignedIconWrapper}>
                      <div
                        css={[
                          styles.iconContainer,
                          styles.outerEndAlignedIconWrapper,
                        ]}>
                        <NavIcon
                          icon={getTenderIconArichive(record.dbFolder?.option)}
                        />
                      </div>
                    </div>
                  </Tooltip>
                )}
                {/* Tender folder displaying logic */}
                {isTenderFolder && !isRootTenderFolder && (
                  <div css={styles.outerEndAlignedIconWrapper}>
                    <div style={{ display: 'flex' }}>
                      {
                        <IconButton
                          iconStyle={type.iconStyle}
                          icon={type.icon}
                          iconSecond={
                            record.state === DB_STATE.REMOVED
                              ? ['fad', 'trash']
                              : null
                          }
                          colorSecondIcon={'red'}
                          customStyleSecondIcon={{ left: '12px', top: '10px' }}
                          color={'tenderBlue'}
                          customStyle={styles.icons}
                          tooltip={intl.formatMessage(
                            { id: type.icon },
                            { type: record.extension?.toUpperCase() }
                          )}
                          badge={
                            record.state !== DB_STATE.REMOVED &&
                            type.is_empty &&
                            '0'
                          }
                          backgroundOff
                        />
                      }
                    </div>
                  </div>
                )}
                {/* Tender file displaying logic (UNIFIED, no duplicates) */}
                {!isTenderFolder && (
                  <div css={styles.outerEndAlignedIconWrapper}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* 1) Eye preview for PDF/IMG */}
                      <div css={styles.iconContainer}>
                        {isPreviewableFormat && (
                          <IconButton
                            click={() => {
                              if (record.dbFile?.versions?.length) {
                                setFileToView({ ...record.dbFile });
                              } else {
                                message.error(
                                  intl.formatMessage({
                                    id: 'file-preview-error',
                                  })
                                );
                              }
                            }}
                            iconStyle={type.iconStyle}
                            icon={['far', 'eye']}
                            color="red"
                          />
                        )}
                      </div>

                      {/* 2) SVF transcribe/view icon (only if convertible or viewable) */}
                      {(() => {
                        const version = fileVersionsContext[record.id];
                        const hasModel = version?.extension_svf_model != null;
                        const isConvertible =
                          version?.is_svf_convertible === true;
                        const isViewable =
                          version?.extension_svf_model?.state === 7;
                        const shouldShow = isConvertible || isViewable;
                        if (!shouldShow) return null;

                        return (
                          <SVFIconType
                            key={`svf-${record.id}-${
                              version?.extension_svf_model?.state ?? 'none'
                            }`}
                            version={version}
                            extension={record.extension}
                            variant="sidebar"
                            disabled={false}
                            customTooltip={(() => {
                              const state = version?.extension_svf_model?.state;
                              if (!hasModel && isConvertible)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              if ([1, 2, 4].includes(state as number))
                                return intl.formatMessage({
                                  id: 'svf-view-file-progress',
                                });
                              if (state === 7)
                                return intl.formatMessage({
                                  id: 'svf-view-file-window',
                                });
                              if (state === 5)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              return intl.formatMessage({
                                id: 'svf-view-file',
                              });
                            })()}
                            onClick={() => {
                              if (!record.dbFile) {
                                message.error(
                                  intl.formatMessage({
                                    id: 'file-preview-error',
                                  })
                                );
                                return;
                              }
                              const v = fileVersionsContext[record.id];
                              const ready = v?.extension_svf_model?.state === 7;

                              if (ready) {
                                setFileToView({ ...record.dbFile });
                                return;
                              }
                              if (
                                openSVFTranscribe &&
                                (isConvertible ||
                                  v?.extension_svf_model?.state === 5)
                              ) {
                                openSVFTranscribe({ ...record.dbFile });
                                setFileToView(undefined);
                              }
                            }}
                            type={type}
                          />
                        );
                      })()}

                      {/* 3) File/CAD icon (single source of truth) */}
                      {(() => {
                        const ext = record.extension?.toLowerCase() || '';
                        const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

                        if (isCadFile) {
                          return (
                            <SVFIconType
                              key={`file-icon-${record.id}`}
                              version={fileVersionsContext[record.id]}
                              extension={record.extension}
                              variant="table"
                              customTooltip={intl.formatMessage(
                                { id: type.icon },
                                { type: record.extension?.toUpperCase() }
                              )}
                              onClick={() => {
                                // no-op for CAD file icon click (optional)
                              }}
                              type={type}
                            />
                          );
                        }

                        return (
                          <IconButton
                            iconStyle={type.iconStyle}
                            icon={type.icon}
                            iconSecond={
                              record.state === DB_STATE.REMOVED ||
                              record.state === DB_STATE.DELETED
                                ? ['fad', 'trash']
                                : null
                            }
                            colorSecondIcon="red"
                            customStyleSecondIcon={{
                              left: '12px',
                              top: '10px',
                            }}
                            color={type.color}
                            customStyle={styles.icons}
                            tooltip={intl.formatMessage(
                              { id: type.icon },
                              { type: record.extension?.toUpperCase() }
                            )}
                            badge={
                              record.state !== DB_STATE.REMOVED &&
                              record.state !== DB_STATE.DELETED &&
                              type.is_empty &&
                              '0'
                            }
                            backgroundOff
                          />
                        );
                      })()}
                    </div>
                  </div>
                )}
              </Fragment>
            ),
          };
        },
        sorter: sortByType,
      },
      // STAGE OLD PREVIEW
      // {
      //   title: intl.formatMessage({ id: 'type' }),
      //   dataIndex: 'type',
      //   width: 60,
      //   align: 'center',
      //   render: (type, record) => {
      //     return {
      //       props: {
      //         style: getBackgroundRow(record),
      //       },
      //       children: (
      //         <div style={{ display: 'flex', justifyContent: 'center' }}>
      //           <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      //             <div style={{ width: 30, height: 30 }}>
      //               {(type.icon === 'file-pdf' ||
      //                 type.icon === 'file-image') && (
      //                 <IconButton
      //                   click={() => {
      //                     if (
      //                       record.dbFile?.versions &&
      //                       record.dbFile?.versions?.length > 0
      //                     ) {
      //                       setFileToView({ ...record.dbFile });
      //                     } else {
      //                       message.error(
      //                         intl.formatMessage({ id: 'file-preview-error' })
      //                       );
      //                     }
      //                   }}
      //                   iconStyle={type.iconStyle}
      //                   icon={['far', 'eye']}
      //                   color="red"
      //                 />
      //               )}
      //               {(() => {
      //                 const version = fileVersionsContext[record.id];
      //                 const hasModel = version?.extension_svf_model != null;
      //                 const isConvertible =
      //                   version?.is_svf_convertible === true;
      //                 const isViewable =
      //                   version?.extension_svf_model?.state === 7;
      //                 // Show icon if convertible or has model (for state 7, even if not convertible)
      //                 const shouldShow = isConvertible || isViewable;
      //
      //                 if (!shouldShow) {
      //                   return null;
      //                 }
      //
      //                 return (
      //                   <SVFIconType
      //                     key={`svf-${record.id}-${
      //                       version?.extension_svf_model?.state || 'none'
      //                     }`}
      //                     version={version}
      //                     extension={record.extension}
      //                     variant="sidebar"
      //                     disabled={false} // Always enabled if shown
      //                     customTooltip={(() => {
      //                       const state = version?.extension_svf_model?.state;
      //                       if (!hasModel && isConvertible) {
      //                         return intl.formatMessage({
      //                           id: 'svf-view-file-convert',
      //                         });
      //                       }
      //                       if ([1, 2, 4].includes(state as number)) {
      //                         return intl.formatMessage({
      //                           id: 'svf-view-file-progress',
      //                         });
      //                       }
      //                       if (state === 7) {
      //                         return intl.formatMessage({
      //                           id: 'svf-view-file-window',
      //                         });
      //                       }
      //                       if (state === 5) {
      //                         return intl.formatMessage({
      //                           id: 'svf-view-file-convert',
      //                         });
      //                       }
      //                       return intl.formatMessage({ id: 'svf-view-file' });
      //                     })()}
      //                     onClick={() => {
      //                       if (record.dbFile == null) {
      //                         message.error(
      //                           intl.formatMessage({ id: 'file-preview-error' })
      //                         );
      //                         return;
      //                       }
      //                       const version = fileVersionsContext[record.id];
      //                       if (version?.extension_svf_model?.state === 7) {
      //                         setFileToView({ ...record.dbFile }); // Open viewer for state 7, regardless of is_svf_convertible
      //                         return;
      //                       }
      //                       if (
      //                         openSVFTranscribe != null &&
      //                         (isConvertible ||
      //                           version?.extension_svf_model?.state === 5)
      //                       ) {
      //                         openSVFTranscribe({ ...record.dbFile }); // Trigger transcribe for convertible files or retry on state 5
      //                         setFileToView(undefined);
      //                       }
      //                     }}
      //                     type={type}
      //                   />
      //                 );
      //               })()}
      //             </div>
      //             {(() => {
      //               const extension = record.extension?.toLowerCase() || '';
      //               const isCadFile = ['rvt', 'dwg', 'ifc'].includes(extension);
      //
      //               if (isCadFile) {
      //                 return (
      //                   <SVFIconType
      //                     key={`file-icon-${record.id}`}
      //                     version={fileVersionsContext[record.id]}
      //                     extension={record.extension}
      //                     variant="table"
      //                     customTooltip={intl.formatMessage(
      //                       { id: type.icon },
      //                       { type: record.extension?.toUpperCase() }
      //                     )}
      //                     onClick={() => {
      //                       // File icon click - no action
      //                     }}
      //                     type={type}
      //                   />
      //                 );
      //               } else {
      //                 // Show generic file icon for other files
      //                 return (
      //                   <IconButton
      //                     iconStyle={type.iconStyle}
      //                     icon={type.icon}
      //                     iconSecond={
      //                       record.state === DB_STATE.DELETED
      //                         ? ['fad', 'trash']
      //                         : null
      //                     }
      //                     colorSecondIcon={'red'}
      //                     customStyleSecondIcon={{ left: '12px', top: '10px' }}
      //                     color={type.color}
      //                     customStyle={styles.icons}
      //                     tooltip={intl.formatMessage(
      //                       { id: type.icon },
      //                       { type: record.extension?.toUpperCase() }
      //                     )}
      //                     badge={
      //                       record.state !== DB_STATE.DELETED &&
      //                       type.is_empty &&
      //                       '0'
      //                     }
      //                     backgroundOff
      //                   />
      //                 );
      //               }
      //             })()}
      //           </div>
      //         </div>
      //       ),
      //     };
      //   },
      //   sorter: sortByType,
      // },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {name}
                {record.favourite && (
                  <IconButton
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        ellipsis: true,
        render: (_, record) => {
          // fileVersionsContext[record.id]?.description
          const selectedVersion = record.dbFile?.versions?.find(
            (v) => v.id === fileVersionsContext[record.id]?.id
          );
          // console.log(
          //   `selectedVersion: ${selectedVersion}`,
          //   fileVersionsContext[record.id]
          // );
          // console.log('ALL versions', record);
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children:
              //@ts-ignore
              fileVersionsContext[record.id]
                ?.registration_instance_description ??
              fileVersionsContext[record.id]?.description ??
              record.dbFile?.description ??
              record.description,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'revision' }),
        dataIndex: 'dbFile',
        sorter: (a, b) => sortByRevision(a, b),
        width: 175,
        ellipsis: true,
        render: (x, record) => {
          // console.log(`fileVersionsContext`, fileVersionsContext[record.id]);
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children:
              // fileVersionsContext[record.id]?.version ||
              // (record.dbFile &&
              //   record.dbFile.versions &&
              //   record.dbFile.versions[0]?.version),
              fileVersionsContext[record.id]?.version === null
                ? ''
                : fileVersionsContext[record.id]?.version ??
                  record.dbFile?.versions?.[0]?.version,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'tags' }),
        dataIndex: 'tags',
        width: 215,
        render: (tag, record) => {
          const tagsArray = record?.dbFile?.tags || [];
          const tagsDataActive = tagsData?.filter(
            (tag) => tag.state !== DB_STATE.REMOVED
          );
          const tags = getTagsForFile(tagsArray, tagsDataActive);

          if (!record.dbFile || !tags) {
            return {
              props: {
                style: getBackgroundRow(record),
              },
            };
          }

          const maxVisibleTags = 2;
          const hiddenCount =
            tags.length > maxVisibleTags ? tags.length - maxVisibleTags : 0;

          return {
            children: (
              <Fragment>
                {tags.slice(0, maxVisibleTags).map((tagItem, index) => {
                  const tag = String(tagItem);
                  return (
                    <TagAntd key={index} color={'#1f5fa6'}>
                      <Tooltip title={tag}>
                        {tag.length > 8 ? `${tag.substring(0, 8)}...` : tag}
                      </Tooltip>
                    </TagAntd>
                  );
                })}
                {hiddenCount > 0 && (
                  <Popover
                    overlayStyle={{ minWidth: 100, maxWidth: 250 }}
                    content={
                      <div
                        css={{
                          padding: '10px',
                        }}>
                        <h3>
                          <b>{intl.formatMessage({ id: 'tags' })}</b>
                        </h3>
                        {tags.map((tag, index) => (
                          <TagAntd
                            style={{ marginBottom: '4px' }}
                            key={index}
                            color={'#1f5fa6'}>
                            {tag}
                          </TagAntd>
                        ))}
                      </div>
                    }>
                    <TagAntd>+{hiddenCount}</TagAntd>
                  </Popover>
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: (
          <IconButton
            containerStyle={{ minHeight: '18px' }}
            icon={['far', 'envelope']}
            backgroundOff
          />
        ),
        dataIndex: 'notifications',
        width: 60,
        align: 'center',
        render: (text, element, index) => {
          return {
            props: {
              style: getBackgroundRow(element),
            },
            children: (
              <IconButton
                icon={
                  element.notifications
                    ? ['fad', 'check-circle']
                    : ['far', 'circle']
                }
                color={element.notifications ? 'green' : 'darkGray'}
                customStyle={[styles.icons, styles.small]}
                backgroundOff
                click={async () => {
                  if (
                    window.sessionStorage.getItem('appState') !== 'timeMachine'
                  )
                    element.folder
                      ? doUpdateFolderNotifications({
                          projectId: Number(projectId),
                          folderId: element.id,
                          notify: !element.notifications,
                          recursive: false,
                        })
                      : doUpdateFileNotifications({
                          projectId: Number(projectId),
                          versionedFileId: element.id,
                          notify: !element.notifications,
                        });
                }}
              />
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'ver' }),
        dataIndex: 'dbFile',
        width: 80,
        align: 'center',
        onCell: () => ({ style: { padding: '4px 10px' } }),
        render: (file, element) => {
          return {
            props: {
              style: getBackgroundRow(element),
            },
            children: file && <VersionSelect file={file} />,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'modified-date' }),

        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),

        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile.versions[0]?.updated_at;
          }
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: formatDate(modified, 'short'),
          };
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      tagsData,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  const selectionDisabled = permissionLevel < RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // if root, use folder permission
    if (folderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return selectionDisabled || record.disabled;
  };

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const filteredColumns = (columns as ColumnType<TableEntry>[]).filter(
    (column) => {
      return column?.dataIndex !== 'tags';
    }
  );

  const updatedColumns = !isTagsOn ? filteredColumns : columns;

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  // Empty state logic for archive (isArchiveRoot is declared earlier,
  // alongside projectBuilderQuery, which reuses the same condition)
  const isEmpty = foldersQuery.data?.length === 0 && !foldersQuery.isLoading;

  const emptyText = useMemo(() => {
    if (isArchiveRoot) {
      // Variant A: Non-empty archive (folders exist in Project Builder but not visible)
      if (hasArchiveFolders) {
        // Sub-case A1: User is NOT a superuser - show simple empty state
        if (!onNavigateToProjectBuilder) {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '40px 20px',
                gap: '16px',
              }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({ id: 'project-is-empty' })}
              />
            </div>
          );
        }

        // Sub-case A2: User IS a superuser - show action buttons
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px 20px',
              gap: '16px',
            }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {intl.formatMessage({
                    id: 'archive-has-folders-description-line1',
                  })}
                  <br />
                  {intl.formatMessage({
                    id: 'archive-has-folders-description-line2',
                  })}
                </span>
              }
            />
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}>
              <Button
                type="primary"
                onClick={onNavigateToProjectBuilder}
                style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  icon={['far', 'folder-tree']}
                  backgroundOff
                  customStyle={{ marginRight: 8, padding: 0 }}
                />
                {intl.formatMessage({ id: 'go-to-project-builder' })}
              </Button>
              <Button
                onClick={onOpenPermissions}
                style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  icon={['far', 'file-user']}
                  backgroundOff
                  customStyle={{ marginRight: 8, padding: 0 }}
                />
                {intl.formatMessage({ id: 'manage-permissions' })}
              </Button>
            </div>
          </div>
        );
      }

      // Variant B: Empty archive + superuser
      if (isEmpty && showInitializeButton && onNavigateToProjectBuilder) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px 20px',
              gap: '16px',
            }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({
                id: 'empty-archive-description-superuser',
              })}
            />
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}>
              <Button
                type="primary"
                onClick={onNavigateToProjectBuilder}
                style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  icon={['far', 'folder-tree']}
                  backgroundOff
                  customStyle={{ marginRight: 8, padding: 0 }}
                />
                {intl.formatMessage({ id: 'go-to-project-builder' })}
              </Button>
              <Button
                onClick={onInitializeFromTemplate}
                style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  icon={['far', 'folder-upload']}
                  backgroundOff
                  customStyle={{ marginRight: 8, padding: 0 }}
                />
                {intl.formatMessage({ id: 'initialize-from-template' })}
              </Button>
            </div>
          </div>
        );
      }

      // Variant C: Empty archive + regular user or time machine (simple empty message, no buttons)
      if (isEmpty) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px 20px',
            }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: 'project-is-empty' })}
            />
          </div>
        );
      }
    }
    return undefined;
  }, [
    isEmpty,
    hasArchiveFolders,
    isArchiveRoot,
    showInitializeButton,
    intl,
    onInitializeFromTemplate,
    onNavigateToProjectBuilder,
    onOpenPermissions,
  ]);

  const content = (
    <Fragment>
      <Table<TableEntry>
        css={[
          styles.table,
          // tableProps.scroll?.y && {
          //   '.ant-table-body': {
          //     height: tableProps.scroll.y,
          //   },
          // },
        ]}
        showSorterTooltip={false}
        className={`main-content-window ${
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          'timeMachine-table'
        } ${suMode && 'su-table'}`}
        pagination={false}
        bordered
        locale={emptyText ? { emptyText } : undefined}
        columns={updatedColumns}
        dataSource={[...filteredFolders, ...filteredFiles]}
        loading={
          filesQuery.isLoading || foldersQuery.isLoading || foldersQuery.isIdle
        }
        rowClassName={(record) => {
          let className = suMode ? 'su-row-table ' : '';
          if (window.sessionStorage.getItem('appState') === 'timeMachine') {
            className += 'timeMachine-row-table' + ' ';
          }
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          renderCell: (type, record, element, node) => {
            return {
              props: {
                style: getBackgroundRow(record),
              },
              children: node,
            };
          },
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: isRecordSelectDisabled(record),
          }),
        }}
        onRow={(record, index) => ({
          ...handleRow(record, index),
          onClick: (event) => {
            if (event.shiftKey) {
              return handleSelectShift(record, index);
            }
            const onClickAction = handleRow(record, index).onClick;
            if (onClickAction) {
              return onClickAction(event);
            }
          },
        })}
        {...tableProps}
      />
      <div
        onContextMenu={() => {
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  if (fileToView) {
    return (
      <div
        style={{
          height: tableProps.scroll && tableProps.scroll.y,
          maxHeight: tableProps.scroll && tableProps.scroll.y,
          display: 'flex',
          flexDirection: 'column',
          width: '1230px',
        }}>
        {content}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}>
      <div
        style={{
          minWidth: 1150,
          // width: 1065,
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 10px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 10px)`,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        {content}
      </div>
    </div>
  );
};

export const getIsFilePreviewableByIcon = (icon: string) => {
  return icon === 'file-pdf' || icon === 'file-image';
};
